/**
 * inventory-create/types — 재고 등록 페이지(/inventory/new/:tab)의 상태. 탭 둘(part · server), 탭마다 상태 덩어리 하나.
 * 서버 한 대 = 절 셋(① 카탈로그 ② 실물 ③ 구성). ③ 은 ServerConfigEditor 가 든다 — 여기엔 없다.
 */
import type { CatalogMatch, ChassisDetail, ChassisModelDetail, ChassisModelView, ChassisSkuView, HcltProductView, PartCandidate, PreconfigView } from '../../api/client';

export const INVENTORY_CREATE_TABS = ['part', 'server'] as const;
export type  InventoryCreateTab    = typeof INVENTORY_CREATE_TABS[number];

/** ① 카탈로그 — 무엇을 골랐나. 기종 · SKU · BTO 는 행 그대로. sku 와 bto 는 둘 중 하나만(CTO 섀시냐 BTO 냐), 둘 다 null = SKU 모름. productId 는 BIGSERIAL 이라 글 */
export interface CatalogChoice { query: string; model: ChassisModelView | null; sku: ChassisSkuView | null; bto: PreconfigView | null; productId: string | null }

/** ① 의 단 셋 — 기종 → SKU → 사양. 접히는 계단: 펼쳐진 단은 하나뿐, 고르면 한 줄로 접히고 다음 단이 펼쳐진다. null = 셋 다 접힘 */
export const CATALOG_STEPS = ['model', 'sku', 'product'] as const;
export type  CatalogStep   = typeof CATALOG_STEPS[number];

/** ① 카탈로그 — 고를 것들과 고른 것의 상세. 서버가 준 것. query 가 바뀌면 matches(자동완성)를, 모델명이 바뀌면 skus · products · modelDetail 을, SKU 가 바뀌면 skuDetail 을 다시 받는다 */
export interface CatalogOptions {
  matches:     CatalogMatch[];
  skus:        ChassisSkuView[];
  preconfigs:  PreconfigView[];             // 그 기종의 BTO — SKU 단에서 CTO 칩과 나란히(2026-10-05)
  products:    HcltProductView[];
  modelDetail: ChassisModelDetail | null;   // 고른 모델명의 열 전부 — 받기 전엔 null
  skuDetail:   ChassisDetail | null;        // 고른 SKU 의 열 전부
}

/** ② 실물 — 저장 때 inventory_unit 한 행(+ inventory_unit_hpe)이 될 값. 열 이름은 DB 그대로. 숫자도 입력 중엔 글 */
export interface UnitInput {
  serial_number: string; condition: string; warehouse: string; zone: string;
  cost_amount: string; cost_currency: 'KRW' | 'USD'; origin: 'found' | 'legacy';
  security_id: string; tracking_number: string; manufacture_date: string;
}

/** 스캔 목록(2026-10-06) — 바코드 기기로 찍은 번호. 시리얼은 스캐너가 진실, 사진은 PN 만 준다 */
export const SCAN_TYPES = ['serial', 'security_id', 'tracking_number', 'service_tag', 'ppid', 'wwn'] as const;   // unit 설계의 번호 세 층과 같은 이름
export type  ScanType   = typeof SCAN_TYPES[number];
export const SCAN_BRANDS = ['HPE', 'Dell', 'Lenovo', 'Samsung', 'Seagate', '기타'] as const;
export type  ScanBrand   = typeof SCAN_BRANDS[number];
export type  PnSource    = 'purchase' | 'photo' | 'manual';        // 카드의 PN 이 어디서 왔나 — 구매(발주 줄) · 사진 · 수기

/** 스캔 버블 — 실물 한 장. 찍은 번호가 여럿 붙는다(시리얼 없이 SID 만일 수도). target = 들어간 카드의 key, null = 풀 */
export interface ScanLine { numbers: Partial<Record<ScanType, string>>; brand: ScanBrand; target: string | null; scanned_at: string }

/** 구매 품목 카드 — 버블이 들어갈 자리. qty = 자리 수(구매 줄 · 사진), null = 수 모름(PN 직접). category 는 라벨 번호 틀을 고르는 열쇠(모르면 null → 시리얼 하나) */
export interface ScanTarget { key: string; brand: ScanBrand; option_part: string; description: string; category: string | null; qty: number | null; source: PnSource }

/** 라벨 번호 틀 — 그 카테고리 · 브랜드의 라벨에 찍을 번호의 **자리 순서**. 꼴 판정은 없다(현민 2026-10-06) — 찍는 대로 1 → 2 → 3 에 들어가고, 틀린 자리는 칩을 끌어 바꾼다.
 *  required = 앞에서부터 필수인 자리 수(HPE 섀시 시리얼 · SID 둘, CT 는 선택). 자리가 다 차면 버블이 닫히고, 선택 자리가 없는 라벨은 빈 엔터 · 확정으로 닫는다. brand null = 브랜드 무관 */
export interface LabelNumberFrame { category: string; brand: ScanBrand | null; types: readonly ScanType[]; required: number }
export const LABEL_NUMBER_FRAMES: readonly LabelNumberFrame[] = [
  { category: 'chassis', brand: 'HPE',  types: ['serial', 'security_id', 'tracking_number'], required: 2 },
  { category: 'chassis', brand: 'Dell', types: ['service_tag', 'ppid'],                      required: 1 },
  { category: 'ssd',     brand: null,   types: ['serial', 'wwn'],                            required: 1 },
  { category: 'hdd',     brand: null,   types: ['serial', 'wwn'],                            required: 1 },
];
export const DEFAULT_FRAME: LabelNumberFrame = { category: '', brand: null, types: ['serial'], required: 1 };   // 틀에 없는 카테고리(메모리 · CPU · 컨트롤러 · NIC · PSU) · PN 직접 카드 · 풀(버블 = 바코드 하나)

/** brand = 다음 스캔에 붙을 브랜드(셀렉터). activeTarget = 지금 찍는 카드(null = 풀로). open = 아직 번호를 더 받는 버블(인덱스). selected = 풀 · 카드에서 고른 버블들(옮기기). isPurchaseOpen = 구매 줄 패널 */
export interface ScanList { brand: ScanBrand; lines: ScanLine[]; targets: ScanTarget[]; activeTarget: string | null; open: number | null; selected: Set<number>; isPurchaseOpen: boolean }

/** ⓪ 사진이 읽은 줄 하나 — 섀시 라벨의 칸 하나(PN · S/N · SID · CT · 제조일) 또는 부품 라벨 한 종류(PN × 수량). 사람이 ✕ 로 뺄 수 있다(keep) */
export type PhotoLine =
  | { kind: 'chassis'; field: 'catalog.query' | 'unit.serial_number' | 'unit.security_id' | 'unit.tracking_number' | 'unit.manufacture_date'; label: string; value: string; keep: boolean }
  | { kind: 'part'; part: PartCandidate; qty: number; keep: boolean };

/** ⓪ 읽은 것 — 상태 넷: 아직(idle) · 읽는 중(reading) · 읽음(read — 패널에 줄이 떠 있고 사람이 확인 전) · 확인됨(confirmed — 칸 · 트레이에 들어갔다) */
export const PHOTO_STATUSES = ['idle', 'reading', 'read', 'confirmed'] as const;
export type  PhotoStatus    = typeof PHOTO_STATUSES[number];
export interface PhotoRead { status: PhotoStatus; lines: PhotoLine[]; error: string }

/** 진행 띠의 네 걸음 — 저장된 값이 아니라 상태에서 읽는다(위저드가 아니다, 코드에 단계 값이 없다). 사진 → 확인(① 이 정해짐) → 채우기(③ 에 든 것이 있음) → 저장 */
export const REGISTER_STEPS = ['photo', 'confirm', 'fill', 'save'] as const;
export type  RegisterStep   = typeof REGISTER_STEPS[number];

/** 저장 결과 카드 — 저장이 만든 것. 목업: 번호는 가짜 */
export interface SaveResult { unit_id: number; hclt_number: string; installed_count: number; included_count: number; is_new_product: boolean; inspect_done: number; inspect_total: number }

/** ④ 점검 후보 — 제품팀이 가릴 후보(2026-10-06 목업). type: 체크만 · 고르기 · 적기. scope: 한 대마다 다름(unit) · 같은 PN 이면 늘 같음(pn) */
export type InspectType  = 'confirm' | 'choose' | 'text';
export type InspectScope = 'unit' | 'pn';
export interface InspectCandidate      { key: string; label: string; type: InspectType; scope: InspectScope; options: string[]; hint: string }
export interface InspectCandidateGroup { title: string; candidates: InspectCandidate[] }
/** 후보 하나에 적은 값. 목업은 페이지 상태에만 — 저장 안 됨 */
export interface InspectAnswer { done: boolean; choice: string; text: string }

export interface ServerSection {
  catalog:        CatalogChoice;
  catalogOptions: CatalogOptions;
  catalogStep:    CatalogStep | null;   // 지금 펼쳐진 단
  unit:           UnitInput;
  fromPhoto:    Set<string>;     // 📷 로 채워진 칸 이름 — 'catalog.query' · 'unit.serial_number'. 저장 안 됨, 표시용
  photo:        PhotoRead;
  scan:         ScanList;                                   // 스캔 목록 — ⓪ 앞. 버블 수 = 실물 수
  configCount:  { installed: number; included: number };   // ③ 에 든 것의 수 — 진행 띠 · 저장 결과 재료(편집기가 config:change 로 알려 준다)
  saved:        SaveResult | null;
  inspect:      Record<string, InspectAnswer>;             // ④ 점검 후보의 답. 키 = InspectCandidate.key
}

export interface PartSection { /* A1-파트 — 다음 */ note: string }

export interface InventoryCreateData { tab: InventoryCreateTab; server: ServerSection; part: PartSection }
